
import { useI18n } from "vue-i18n";
import { defineComponent, ref, reactive, watch, onMounted, computed, watchEffect } from "vue";
import { useRoute } from "vue-router";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import * as Cashier from "@/api/cashier";
import useClipboard from "vue-clipboard3";
import {
  Field,
  Button,
  DropdownMenu,
  DropdownItem,
  Cell,
  CellGroup,
  Toast,
  Loading,
  Overlay,
  Icon,
  Popover,
  ActionSheet,
  Skeleton
} from "vant";

import {
  InterfaceUI,
  CoinMapCountry,
  Languages,
  CalcPayOutSteps,
  FormatDateZone,
  CreateOrderParams,
  FormData,
  OptionObject,
  CashPayOutOrderStatus,
  ChannelObject,
  ChannelCurrencyRatesObject,
  SupportedBanksList,
  SupportedCountries
} from "./interface";

export default defineComponent({
  name: "payin",
  components: {
    VueQrcode,
    [Field.name]: Field,
    [Button.name]: Button,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Cell.name]: Cell,
    [CellGroup.name]: CellGroup,
    [Toast.name]: Toast,
    [Loading.name]: Loading,
    [Overlay.name]: Overlay,
    [Icon.name]: Icon,
    [Popover.name]: Popover,
    [ActionSheet.name]: ActionSheet,
    [Skeleton.nane]: Skeleton
  },
  setup() {
    const route = useRoute();
    const { t, locale } = useI18n();
    const { lang, k, from } = route.query;

    const showSupplierSheet = ref(false);
    const showBankSheet = ref(false);
    const result = ref(false);
    const showPopover = ref(false);
    const currentLang = ref();

    currentLang.value = lang ?? "en";
    let data = reactive({
      dev: process.env.VUE_APP_BUILD_ENV,
      InterfaceUI,
      Languages,
      FormData,
      Search: {
        supplier: "",
        bank: ""
      }
    });

    const changeLang = item => {
      locale.value = item.value;
    };

    watch(
      currentLang,
      newLang => {
        locale.value = newLang;
      },
      {
        immediate: true
      }
    );
    const getLangName = computed(() => {
      return (
        data.Languages.find(x => {
          return x.value === locale.value;
        })?.text || ""
      );
    });

    // 复制
    const { toClipboard } = useClipboard();
    const copy = async (text: string) => {
      try {
        await toClipboard(text);
        Toast({
          message: t("defray.copied"),
          overlay: false,
          duration: 800
        });
      } catch (e) {
        console.error(e);
      }
    };

    /**
     * 获取通道列表
     */
    const __CashChannelList = ref<Array<ChannelObject>>([]);
    const getChannelList = async () => {
      const loadingToast = Toast({
        message: "",
        type: "loading",
        duration: 0
      });
      const res = await Cashier.CashChannelList();
      loadingToast?.close();
      if (res?.code == 200) {
        let list: [ChannelObject] = res?.data || [];
        __CashChannelList.value = list;
        await getOrderInfo();
        GetChannelListByCoin();
      } else {
        Toast({
          message: res?.message || t("error"),
          overlay: false,
          duration: 2000
        });
      }
    };

    /**
     * 通过币种筛选出支持的通道列表
     */
    const CashChannelList = ref<Array<ChannelObject>>([]);
    const GetChannelListByCoin = () => {
      let list =
        __CashChannelList.value.filter((x: ChannelObject) => {
          return x.cashChannelCurrencyRateSetting.find((channel: ChannelCurrencyRatesObject) => {
            return channel?.currency === data.FormData.currency && channel?.flowDirection === 1;
          });
        }) || [];
      CashChannelList.value = list;
      if (list.length) {
        data.FormData.channelCode = list[0]?.code;
      }
    };

    /**
     * 通过通道获取对应的支付方式
     */
    const PaymentTypeList = computed(() => {
      if (!data.FormData.channelCode) return;
      let cashChannelCurrencyRateSetting =
        CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let __data =
        cashChannelCurrencyRateSetting.filter(x => {
          return (
            x.currency == data.FormData.currency && x.flowDirection === 1 && x.supportedChannelType
          );
        }) || [];

      let list: Array<ChannelCurrencyRatesObject> = [];
      let temp = {};
      __data.map(x => {
        if (!temp[x.supportedChannelType]) {
          temp[x.supportedChannelType] = x.supportedChannelType;
          list.push(x);
        }
      });

      if (list.length > 0) {
        let item = list[0] || {};
        data.FormData.paymentType = item?.supportedChannelType;
        data.FormData.transactionFee = item?.transactionFee || 0;
      }
      // 处理没有任何渠道类型时，费率问题
      else {
        __data =
          cashChannelCurrencyRateSetting.filter(x => {
            return x.currency == data.FormData.currency && x.flowDirection == 0;
          }) || [];
        if (__data.length) {
          data.FormData.transactionFee = __data[0]?.transactionFee || 0;
        }
      }

      return list;
    });

    /**
     * 通过编码查找对应的名称
     * @param code 编码Code
     * @param list 列表数据
     */
    const getNameByCode = (code: string, list: Array<OptionObject>) => {
      return (
        list.find((x: OptionObject) => {
          return x.value == code;
        })?.name || ""
      );
    };

    // 运营商列表
    const SupplierList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let temp =
        list.filter(x => {
          return (
            x.currency == data.FormData.currency && x.flowDirection === 1 && x.supportedMobileType
          );
        }) || [];

      if (temp.length > 1) {
        return temp.map(x => {
          return {
            name: x.supportedMobileType,
            value: x.supportedMobileType
          };
        });
      } else if (temp.length == 1) {
        let __supportedMobileType = temp[0].supportedMobileType;
        if (Array.isArray(__supportedMobileType)) {
          return __supportedMobileType.map(x => {
            return {
              name: x,
              value: x
            };
          });
        } else {
          return {
            name: __supportedMobileType,
            value: __supportedMobileType
          };
        }
      }
    });
    const onSelectSupplier = val => {
      showSupplierSheet.value = false;
      data.FormData.supplier = val.value;
    };

    // 银行列表
    const BankList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let supportBanks =
        list?.find(x => {
          return (
            x.currency == data.FormData.currency &&
            x.flowDirection === 1 &&
            ["DIRECT", "BANK", "SPEI"].includes(x.supportedChannelType)
          );
        })?.supportBanks || [];

      // 输入筛选过滤
      if (data.Search.bank) {
        supportBanks = supportBanks.filter(x => {
          return (
            x.bankCode.includes(data.Search.bank) ||
            x.bankName.toLocaleLowerCase().includes(data.Search.bank.toLocaleLowerCase())
          );
        });
      }

      return supportBanks.map((x: SupportedBanksList) => {
        return {
          name: x.bankName,
          value: x.bankCode
        };
      });
    });
    const onSelectBank = val => {
      showBankSheet.value = false;
      data.FormData.bankCode = val.value;
    };

    // 国家列表
    const CountryList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.currency;
        })?.cashChannelCurrencyRateSetting || [];

      let temp =
        list.find(x => {
          return (
            x.currency == data.FormData.currency &&
            x.flowDirection === 1 &&
            ["MOMO", "OVO"].includes(x.supportedChannelType)
          );
        })?.supportedCountries || [];

      return temp.map((x: SupportedCountries) => {
        return {
          name: `${x.name} (${x.code})`,
          value: x.code
        };
      });
    });

    /**
     * 获取订单信息
     */
    const getOrderInfo = async () => {
      const loadingToast = Toast({
        message: "",
        type: "loading",
        duration: 0
      });
      const res = await Cashier.GetPayOutOrderInfo();
      loadingToast?.close();
      if (res?.code == 200) {
        data.FormData = Object.assign(data.FormData, res?.data || {});
        if (data.FormData.orderStatus == 1) {
          setTimeout(() => {
            getStatus();
          }, 4000);
        }
      } else {
        Toast({
          message: res?.message || t("error"),
          overlay: false,
          duration: 2000
        });
      }
    };

    // 轮询获取状态
    const getStatus = async () => {
      const res = await Cashier.GetPayOutOrderInfo();
      if (res?.code == 200) {
        data.FormData = Object.assign(data.FormData, res?.data || {});
        switch (data.FormData.orderStatus) {
          // Pending
          case 1:
          case 2:
          case 3:
          case 4:
            setTimeout(() => {
              getStatus();
            }, 10000);
            break;
          // Success
          case 5:
            setTimeout(() => {
              paymentUrl(data.FormData.redirectUrl, "SUCCESS");
            }, 1000);
            break;
          // Fail
          case 6:
          case 7:
            paymentUrl(data.FormData.cancelUrl, "FAIL");
            break;
          // Cancel
          case 8:
            paymentUrl(data.FormData.cancelUrl, "CANCEL");
            break;
        }
      } else {
        setTimeout(() => {
          getStatus();
        }, 5000);
      }
    };

    /**
     * 付款路由操作行为
     **/
    const paymentUrl = (url, type) => {
      if (from == "app") {
        const status = {
          SUCCESS: "CASHIER-TOPUP-SUCCESS",
          FAIL: "CASHIER-TOPUP-FAIL",
          CANCEL: "CASHIER-TOPUP-CANCEL"
        };
        window["BacktrackToApp"]?.postMessage(
          JSON.stringify({
            key: status[type],
            data: null
          })
        );
        return;
      }
      url && (window.location.href = url);
    };

    /**
     * 上一步、下一步
     */
    const step = ref(1);
    const nextStep = (val: boolean) => {
      if (val) {
        if (step.value == steps.value.length) {
          CreateWithdrawOrder();
        } else {
          if (step.value == 2) {
            ValidateInfo();
          } else {
            step.value = step.value + 1;
          }
        }
      } else {
        step.value = step.value - 1;
      }
    };

    // 下一步按钮禁用逻辑
    const isDisableConfirm = computed(() => {
      let result = false;
      if (step.value == 2) {
        if (forms.value.supplier && !data.FormData.supplier) {
          result = true;
        }
        if (forms.value.phoneNumber && !data.FormData.phoneNumber) {
          result = true;
        }
        if (forms.value.bankCode && !data.FormData.bankCode) {
          result = true;
        }
        if (forms.value.bankNumber && !data.FormData.bankNumber) {
          result = true;
        }
        if (forms.value.accountName && !data.FormData.accountName) {
          result = true;
        }
      }

      return result;
    });

    // 计算出每步表单
    const forms = ref({
      paymentType: false,
      supplier: false,
      phoneNumber: false,
      bankCode: false,
      bankNumber: false,
      accountName: false
    });
    const steps = ref([0]);
    watchEffect(() => {
      steps.value = CalcPayOutSteps(
        data.FormData.channelCode,
        data.FormData.paymentType,
        data.FormData.currency
      );

      // Payment Type
      if (
        ["PivotPay", "FacilitaPay", "AlchemyPay"].includes(data.FormData.channelCode) ||
        ["NGN"].includes(data.FormData.currency)
      ) {
        forms.value.paymentType = true;
      } else {
        forms.value.paymentType = false;
      }

      // Supplier
      if (["MOMO"].includes(data.FormData.paymentType)) {
        forms.value.supplier = true;
      } else {
        forms.value.supplier = false;
      }

      // Phone Number
      if (["MOMO", "OVO"].includes(data.FormData.paymentType)) {
        forms.value.phoneNumber = true;
      } else {
        forms.value.phoneNumber = false;
      }

      // 银行卡、银行直连
      if (["BANK", "SPEI", "DIRECT"].includes(data.FormData?.paymentType?.toLocaleUpperCase())) {
        forms.value.bankCode = true;
        forms.value.bankNumber = true;
      } else {
        forms.value.bankCode = false;
        forms.value.bankNumber = false;
      }

      // 印尼出金必填账户名称
      if (["AlchemyPay"].includes(data.FormData?.channelCode)) {
        forms.value.accountName = true;
      } else {
        forms.value.accountName = false;

        // KoraPay 的 NGN 需要检验后显示
        if (
          step.value == 3 &&
          ["MOMO", "OVO"].includes(data.FormData.paymentType) &&
          data.FormData.currency !== "KES"
        ) {
          {
            forms.value.accountName = true;
          }
        }
      }
    });

    /**
     * 校验账户
     * PivotPay 的 KES 和 AlchemyPay 两种情况特殊处理 （不校验）
     **/
    const ValidateInfo = async () => {
      if (isDisableConfirm.value) return;
      if (
        (data.FormData.channelCode == "PivotPay" && data.FormData.currency == "KES") ||
        data.FormData.channelCode == "AlchemyPay"
      ) {
        step.value = step.value + 1;
        return;
      }
      const loadingToast = Toast({
        message: "",
        type: "loading",
        duration: 0
      });
      const res = await Cashier.ValidateInfo({
        channel_code: data.FormData.channelCode,
        payment_type: data.FormData.paymentType,
        account_number:
          data.FormData.paymentType == "MOMO"
            ? data.FormData.phoneNumber
            : data.FormData.bankNumber,
        supplier:
          data.FormData.channelCode === "AlchemyPay" && data.FormData.currency == "MXN"
            ? "MEXICO_PAY"
            : data.FormData.channelCode === "AlchemyPay"
            ? "INDONESIA_PAY"
            : data.FormData.supplier,
        country_code: CoinMapCountry[data.FormData.currency]?.countryCode, // data.FormData.countryCode,
        bank_code: data.FormData.bankCode
      });
      loadingToast.close();
      if (res?.code == 200) {
        let result = res?.data;
        data.FormData.accountName = "";
        if (/true/i.test(result?.accountStatus)) {
          data.FormData.accountName = res?.data?.accountName || "";
          step.value = step.value + 1;
        } else {
          Toast({
            message: t("cashier.validity"),
            overlay: false,
            duration: 2000
          });
        }
      } else {
        Toast({
          message: res?.message || t("cashier.validity"),
          overlay: false,
          duration: 2000
        });
      }
    };

    /**
     * 发起提现申请
     */
    const CreateWithdrawOrder = async () => {
      if (isDisableConfirm.value) return;
      let param: CreateOrderParams = {
        channel_code: data.FormData.channelCode,
        country_code: CoinMapCountry[data.FormData.currency]?.countryCode, // data.FormData.countryCode,
        payment_type: data.FormData.paymentType,
        phone_number: data.FormData.phoneNumber,
        account_name: data.FormData.accountName,
        bank_code: data.FormData.bankCode,
        bank_number: data.FormData.bankNumber,
        supplier:
          data.FormData.channelCode === "AlchemyPay" && data.FormData.currency == "MXN"
            ? "MEXICO_PAY"
            : data.FormData.channelCode === "AlchemyPay"
            ? "INDONESIA_PAY"
            : data.FormData.supplier
      };
      const loadingToast = Toast({
        message: "",
        type: "loading",
        overlay: true,
        duration: 0
      });
      const res = await Cashier.CreateWithdrawOrder(param);
      loadingToast.close();
      if (res?.code == 200) {
        data.FormData.extend = res?.data || {};
        Toast({
          message: res?.message || t("cashier.pending"),
          overlay: false,
          duration: 4000
        });
        getOrderInfo();
      } else {
        Toast({
          message: res?.message || t("cashier.error"),
          overlay: false,
          duration: 4000
        });
      }
    };

    onMounted(() => {
      if (k == undefined || !k) {
        Toast({
          message: t("error"),
          overlay: false,
          duration: 4000
        });
        return;
      }
      getChannelList();
    });

    return {
      locale,
      data,
      currentLang,
      showPopover,
      showSupplierSheet,
      showBankSheet,
      changeLang,
      getLangName,
      FormatDateZone,
      copy,

      paymentUrl,
      isDisableConfirm,
      step,
      steps,
      nextStep,
      forms,
      result,

      CashPayOutOrderStatus,
      CashChannelList,
      PaymentTypeList,
      getNameByCode,

      SupplierList,
      onSelectSupplier,
      BankList,
      onSelectBank
    };
  }
});
